import { useEffect, useRef, useState } from "react";

import { useEntityPreviewAsset } from "../hooks/useEntityPreviewAsset";
import { useEntityPreviewBlobUrl } from "../hooks/useEntityPreviewBlobUrl";
import { RegistryItem } from "../types";
import { getEntityColor } from "../utils";

import { EntityIcon } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";

interface EntityScreenshotPreviewProps {
  item: RegistryItem;
  workspaceId?: number | null;
}

const PreviewSkeleton = () => (
  <div className="absolute inset-0 flex flex-col justify-end p-4">
    <Skeleton className="absolute inset-0 opacity-70" height="100%" borderRadius="0.5rem" />
    <div className="relative space-y-2">
      <Skeleton width="72%" height="0.5rem" borderRadius="999px" className="bg-gray-200/80" />
      <Skeleton width="46%" height="0.5rem" borderRadius="999px" className="bg-gray-200/70" />
    </div>
  </div>
);

export const EntityScreenshotPreview = ({ item, workspaceId }: EntityScreenshotPreviewProps) => {
  const { name, type } = item;
  const { preview, refreshPreview } = useEntityPreviewAsset({ item, workspaceId });
  const imageUrl = preview?.image_url;
  const { resolvedImageUrl, isResolvingBlobUrl } = useEntityPreviewBlobUrl({
    imageUrl,
    contentHash: preview?.content_hash,
  });
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(Boolean(resolvedImageUrl));
  const [isResolvingImage, setIsResolvingImage] = useState(false);
  const requestedPreviewKeyRef = useRef<string | null>(null);
  const isRegenerating = Boolean(preview?.is_regenerating);
  const canShowImage = Boolean(resolvedImageUrl && resolvedImageUrl !== failedImageUrl);
  const shouldShowLoadingPreview =
    isResolvingBlobUrl ||
    isResolvingImage ||
    (preview?.status === "pending" && isRegenerating && !canShowImage);

  useEffect(() => {
    setFailedImageUrl(null);
    setIsResolvingImage(false);
    setIsImageLoading(Boolean(resolvedImageUrl));
  }, [resolvedImageUrl, preview?.content_hash, preview?.entity_version_key]);

  useEffect(() => {
    if (workspaceId === null || workspaceId === undefined) {
      return;
    }

    if (preview?.status === "pending" || preview?.image_url || isResolvingImage) {
      return;
    }

    const requestKey = [
      workspaceId,
      type,
      String(item.id),
      preview?.status ?? "missing",
      preview?.content_hash ?? "",
      preview?.entity_version_key ?? "",
    ].join(":");

    if (requestedPreviewKeyRef.current === requestKey) {
      return;
    }

    requestedPreviewKeyRef.current = requestKey;
    setIsResolvingImage(true);
    void refreshPreview()
      .catch(() => undefined)
      .finally(() => {
        setIsResolvingImage(false);
      });
  }, [
    isResolvingImage,
    item.id,
    preview?.content_hash,
    preview?.entity_version_key,
    preview?.image_url,
    preview?.status,
    refreshPreview,
    type,
    workspaceId,
  ]);

  if (resolvedImageUrl && canShowImage) {
    return (
      <div className="h-full min-h-0 w-full overflow-hidden pb-1.5">
        <div className="relative h-full min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          {isImageLoading && <PreviewSkeleton />}
          <img
            src={resolvedImageUrl}
            alt={`${name} preview`}
            loading="lazy"
            decoding="async"
            className={`block h-full max-h-full w-full max-w-full object-cover object-top transition-opacity duration-150 ease-out ${
              isImageLoading ? "opacity-0" : "opacity-100"
            } ${isRegenerating ? "motion-safe:animate-pulse" : ""}`}
            onLoad={() => setIsImageLoading(false)}
            onError={() => {
              setIsResolvingImage(true);
              void refreshPreview()
                .then(refreshedPreview => {
                  if (!refreshedPreview?.image_url || refreshedPreview.image_url === imageUrl) {
                    setFailedImageUrl(resolvedImageUrl);
                  }
                })
                .catch(() => {
                  setFailedImageUrl(resolvedImageUrl);
                })
                .finally(() => {
                  setIsImageLoading(false);
                  setIsResolvingImage(false);
                });
            }}
          />
        </div>
      </div>
    );
  }

  if (shouldShowLoadingPreview) {
    return (
      <div className="h-full min-h-0 w-full overflow-hidden pb-1.5">
        <div className="relative h-full min-h-0 w-full overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
          <PreviewSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full overflow-hidden pb-1.5">
      <div className="flex h-full min-h-0 w-full items-center justify-center rounded-lg border border-gray-100 bg-gray-50">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-xl ${getEntityColor(type)}`}
        >
          <EntityIcon type={type} size="lg" />
        </div>
      </div>
    </div>
  );
};
